// The row form's foreign-key field (ui:widget "foreignKey", backend operate/row_schema.py): a
// combobox over the rows of the referenced table (GET .../data/tables/{table}/lookup,
// operate/browse.py `lookup`; task 2026-10-06-a-foreign-key-field-offers-the-referenced-rows).
//
//   * On focus, before anything is typed, it lists the referenced keys in key order, each with
//     a readable label (the table's first text column); more than 50: the first 50 and a note
//     that typing narrows them. Typing filters by key or label.
//   * The active option (arrow keys, or the mouse) shows every column of its row.
//   * After a choice, or for a value the row already has, a card under the field shows the
//     referenced row; a typed key no row has says so.
//   * A lookup the server refuses (a key masked as personal data: 403; a shared entity keyed
//     with study_key: 409) shows why, and the field takes a typed value as before.
// Masked columns come masked from the server (***), in the options and the card alike.
import { useEffect, useId, useState } from "react";
import { api } from "../../api";
import Combobox from "../../forms/Combobox";
import { RowDetails, useReferencedRow } from "./ReferencedRow";
import { lookupPath, typed } from "./rowFormUtils";

const MONO = "'JetBrains Mono', monospace";
const NONE = [];
const noteStyle = { fontSize: 12, color: "#4b5563", marginTop: 4 };
const cardStyle = { marginTop: 8, padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8,
                    background: "#f9fafb", maxWidth: "100%", boxSizing: "border-box" };

function statusText(fk, result, q) {
  const n = result.options.length;
  if (n === 0) return q ? `No row of ${fk.table} matches "${q}".` : `${fk.table} has no rows yet.`;
  if (result.more) return `The first ${n} of ${result.total} rows of ${fk.table}, in key order; type to narrow them.`;
  return `${n} row${n === 1 ? "" : "s"} of ${fk.table}${q ? ` match "${q}"` : ""}. Use the arrow keys to choose.`;
}

export default function ForeignKeyWidget({ id, value, onChange, options, schema, disabled, readonly, required,
                                           registry, label }) {
  const fk = options.foreignKey || {};
  const projectId = registry.formContext.projectId;
  const [text, setText] = useState(value == null ? "" : String(value));
  const [q, setQ] = useState("");
  const [wanted, setWanted] = useState(false);          // focused once: the list is loaded from then on
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null);           // {key, row, columns} of the option chosen
  const hintId = `${useId()}-hint`;

  useEffect(() => { setText(value == null ? "" : String(value)); }, [value]);

  // The list: everything on focus, then what the typed text matches; only the newest answer counts.
  useEffect(() => {
    if (!wanted) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      setBusy(true);
      api.get(lookupPath(projectId, fk, { q: q.trim() }))
        .then(r => { if (live) { setResult(r); setError(null); setBusy(false); } })
        .catch(err => { if (live) { setResult(null); setError(err); setBusy(false); } });
    }, q.trim() ? 250 : 0);
    return () => { live = false; clearTimeout(timer); };
  }, [wanted, q, projectId, fk.table, fk.column]);   // eslint-disable-line react-hooks/exhaustive-deps

  const ref = useReferencedRow(projectId, fk, value, { known: picked });

  const pick = o => {
    setPicked({ key: o.key, row: o.row, columns: result ? result.columns : [] });
    setText(String(o.key));
    setQ("");
    onChange(o.key);
  };

  let status = "";
  if (busy) status = "Searching…";
  else if (error) status = error.message;
  else if (wanted && result) status = statusText(fk, result, q.trim());
  const columns = result ? result.columns : [];

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Combobox
            id={id}
            label={label}
            text={text}
            onText={t => { setText(t); setQ(t); setPicked(null); onChange(typed(schema, t)); }}
            options={result && !error ? result.options : NONE}
            optionKey={o => String(o.key)}
            onPick={pick}
            onFocus={() => setWanted(true)}
            openOnFocus
            clearOnEscape={false}
            status={status}
            statusError={!!error}
            busy={busy}
            describedBy={hintId}
            listLabel={`Rows of ${fk.table}`}
            required={required}
            disabled={!!(disabled || readonly)}
            renderOption={(o, { active }) => (
              <>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: "#1a1a1a" }}>{String(o.key)}</span>
                  {o.label != null && o.label !== "" && <span style={{ fontSize: 14, color: "#1a1a1a" }}>{String(o.label)}</span>}
                </div>
                {active && <div style={{ marginTop: 4 }}><RowDetails columns={columns} row={o.row} /></div>}
              </>
            )}
          />
        </div>
      </div>
      <div id={hintId} style={noteStyle}>
        A value of {fk.table}.{fk.column}: pick one of its rows, or type the key.
      </div>
      {ref.row && (
        <section aria-label={`The chosen row of ${fk.table}`} style={cardStyle}>
          <RowDetails columns={ref.columns} row={ref.row} />
        </section>
      )}
      {!ref.row && !ref.loading && !ref.error && ref.key !== null && (
        <div style={{ ...noteStyle, color: "#b45309" }}>
          No row of {fk.table} has {fk.column} {ref.key}.
        </div>
      )}
    </div>
  );
}
